import React, { useRef } from 'react';
import { TArticleBodyBlock, TArticleBodyRendererProps } from './types';
import { ARTICLE_BLOCK_CLASSNAME, QUOTE_HIGHLIGHT_TOOLTIP_ID } from './constants';
import { useArticleHighlightCreate } from '../Highlight/hooks/useCreateArticleHighlight';
import { articleHighlightTooltipFactory } from '../Highlight/classes/ArticleHighlightTooltip';
import { blockRenderers } from './blockRenderers';
import { THighlightBodyBlock } from '../Highlight/types';
import { getBodyBlockWithId } from './services/getBodyBlockWithId';
import { useHighlightContainerHover } from '../Highlight/hooks/useHighlightContainerHover';
import { QuoteHighlightingToolbarContainer } from '../QuoteHighlightingToolbar/QuoteHighlightingToolbarContainer';
import { useTopQuoteReply } from '../Highlight/hooks/useTopQuoteReply';

type TArticleBodyBlockProps = {
  block: TArticleBodyBlock;
  blockHighlightData?: THighlightBodyBlock['data'];
};

const onHighlightCreate = () => {
  const tooltip = articleHighlightTooltipFactory();
  tooltip?.show();
};

const ArticleBodyBlock = ({ block, blockHighlightData }: TArticleBodyBlockProps) => (
  <div className={ARTICLE_BLOCK_CLASSNAME} id={block.id}>
    {blockRenderers[block.type](block?.data, blockHighlightData)}
  </div>
);

const ArticleBodyRenderer = ({ articleId, body }: TArticleBodyRendererProps) => {
  const articleContainer = useRef<HTMLDivElement>(null);
  useHighlightContainerHover(articleContainer);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [quote, highlightDataToShow] = useTopQuoteReply(articleId, body);
  const pickedHighlightData = useArticleHighlightCreate(onHighlightCreate, body);

  return (
    <>
      <div ref={articleContainer}>
        {body?.blocks?.map((block, index) => (
          <ArticleBodyBlock
            key={block.id || index}
            block={block}
            blockHighlightData={getBodyBlockWithId(highlightDataToShow, block.id)?.data}
          />
        ))}
      </div>
      <div
        id={QUOTE_HIGHLIGHT_TOOLTIP_ID}
        role="tooltip"
        className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
      >
        <QuoteHighlightingToolbarContainer
          pickedHighlightData={pickedHighlightData}
          articleId={articleId}
          modalContainer={articleContainer.current}
        />
        <div className="tooltip-arrow" data-popper-arrow />
      </div>
    </>
  );
};

export default ArticleBodyRenderer;
